import { z } from 'zod';

import { assertNever } from '../assertNever';
import {
  BankruptcyAgreedOrderId,
  BankruptcyId,
  DelinquentInterestSettingId,
  EventId,
  FeeId,
  PaymentConfigId,
  PaymentDueId,
  PaymentId,
  QcDocumentId,
  WorkoutPlanId,
} from '../BrandedIds';
import { zodJSInstantDate, zodJSMixedDate, zodJSPlainDate } from '../dates/BrandedDate';
import {
  NonNegativeFloat,
  NonNegativeInteger,
  NonNegativeMonetaryValue,
  PositiveInteger,
  PositiveMonetaryValue,
} from '../validations/fields';

export const WorkoutPlanType = z.enum([
  'paymentDeferment',
  'paymentDeferral',
  'forbearance',
  'flexModification',
  'repaymentPlan',
  'bankruptcyCh13',
]);
export type WorkoutPlanType = z.infer<typeof WorkoutPlanType>;

export const WorkoutPlanStatus = z.enum(['offered', 'active', 'cancelled', 'completed', 'archived']); // archived is for mistakenly added & should not be reported
export type WorkoutPlanStatus = z.infer<typeof WorkoutPlanStatus>;

export const WorkoutPlanBase = z.object({
  id: WorkoutPlanId,
  // Absent on plans created before eventId stamping shipped
  eventId: EventId.optional(),
  status: WorkoutPlanStatus,
  startDate: zodJSPlainDate,
  endDate: zodJSPlainDate, // scheduled end date (aka last day the plan is active. the plan should be marked as completed at the very end of this day)

  createdAt: zodJSInstantDate,
  updatedAt: zodJSInstantDate,

  offeredDate: zodJSPlainDate.optional(),
  acceptedDate: zodJSMixedDate.optional(),
  completedDate: zodJSPlainDate.optional(),
  archivedDate: zodJSPlainDate.optional(),
  cancelledDate: zodJSPlainDate.optional(),
});
export type WorkoutPlanBase = z.infer<typeof WorkoutPlanBase>;

export const WorkoutPlanPaymentDueStatus = z.object({
  amountRemaining: NonNegativeMonetaryValue,
  fulfilledAt: zodJSMixedDate.optional(),
  isLate: z.boolean(),
  paymentMadeIds: z.array(PaymentId),
});
export type WorkoutPlanPaymentDueStatus = z.infer<typeof WorkoutPlanPaymentDueStatus>;

export const WorkoutPlanPaymentDue = z.object({
  total: NonNegativeMonetaryValue, // instead of the regular monthly statement amount, this is the monthly amount due while the workout plan is active
  paymentDate: zodJSPlainDate, // corresponds to the payment due date
  dStatus: WorkoutPlanPaymentDueStatus,
});
export type WorkoutPlanPaymentDue = z.infer<typeof WorkoutPlanPaymentDue>;

export const WorkoutPlanIneligibleModification = z.object({
  type: WorkoutPlanType,
  reason: z.string(),
});
export type WorkoutPlanIneligibleModification = z.infer<typeof WorkoutPlanIneligibleModification>;

export const WorkoutPlanWorkflowInformation = z.object({
  completedBorrowerResponsePackageReceived: z.boolean(),
  /**
   * An "ineligible workout option" is a workout option the lender decided not to offer the borrower
   * These ineligibleModifications are used in the pdf documents only and don't affect willow business logic
   */
  ineligibleModifications: z.array(WorkoutPlanIneligibleModification),
});
export type WorkoutPlanWorkflowInformation = z.infer<typeof WorkoutPlanWorkflowInformation>;

/**
 * PAYMENT DEFERRAL WORKOUT PLAN
 * A payment deferral is offered when a borrower has resolved their financial hardship, but is unable to afford loan reinstate (aka paying back all past due)
 * The selected payment dues are deferred to payoff and are non-interest bearing.
 *
 * The plan is first created in an Offered status, and once accepted it moves to Completed status. So, we bypass an Active status completely for this workout plan.
 */
export const PaymentDeferralReason = z.enum([
  'hardship',
  'naturalDisaster',
  'covid',
  'failedRepaymentPlan',
  'postForbearancePlan',
  'other',
]);
export type PaymentDeferralReason = z.infer<typeof PaymentDeferralReason>;

export const PaymentDeferralAcceptanceMethod = z.enum(['sign', 'call', 'other']); // method in which borrower has to agree to the plan
export type PaymentDeferralAcceptanceMethod = z.infer<typeof PaymentDeferralAcceptanceMethod>;

export const PaymentDeferralCancellationReason = z.enum(['noAgreement', 'reviewingDifferentPlan', 'other']);
export type PaymentDeferralCancellationReason = z.infer<typeof PaymentDeferralCancellationReason>;

export const PaymentDeferralWorkoutPlan = WorkoutPlanBase.extend({
  type: z.literal('paymentDeferral'),
  reason: PaymentDeferralReason,

  // P&I from the start-end statements are deferred
  startStatementDate: zodJSPlainDate,
  endStatementDate: zodJSPlainDate,

  chargeIds: z.array(FeeId), // lenders can select which fees / advances to defer
  paymentDueIds: z.array(PaymentDueId), // payments associated with the selected startStatementDate & endStatementDate

  // If not all past due payments are included in the deferral, lenders will require the excluded PDs are paid before the plan is accepted
  excludedPastDuePaymentDueIds: z.array(PaymentDueId),
  excludedPastDueAmount: NonNegativeMonetaryValue, // will be $0 if all past due payments are included in the deferral

  totalDeferredBalance: NonNegativeMonetaryValue, // sum of quotedDeferredBalances
  quotedDeferredBalances: z.object({
    principal: NonNegativeMonetaryValue,
    interest: NonNegativeMonetaryValue,
    escrowAdvances: NonNegativeMonetaryValue, // (note: statement escrow is not deferred, it is waived.) This comes from a negative escrow balance on the loan that can be brought positive by way of deferral
    charges: NonNegativeMonetaryValue,
  }),

  delinquentInterestSettingId: DelinquentInterestSettingId.optional(), // added if DI is cancelled upon accepting the plan

  escrowAnalysisId: QcDocumentId.optional(), // in case of shortage, information included in the offer letter

  // Set when a Payment Deferral Agreement is generated for the offer; plans created before an agreement have none
  acceptanceMethod: PaymentDeferralAcceptanceMethod.optional(),
  acceptanceMethodOther: z.string().optional(),

  terminationDate: zodJSPlainDate.optional(), // lender-selected date (actual date of draft closure stored at cancelledDate)
  cancellationReason: PaymentDeferralCancellationReason.optional(),
});
export type PaymentDeferralWorkoutPlan = z.infer<typeof PaymentDeferralWorkoutPlan>;

// Followup migrate to v2 version and then delete this definition
export const DefermentWorkoutPlan = WorkoutPlanBase.extend({
  type: z.literal('paymentDeferment'),
  reason: z.enum(['naturalDisaster']).optional(),

  principalAmount: NonNegativeMonetaryValue,
  interestAmount: NonNegativeMonetaryValue,
  feeAmount: NonNegativeMonetaryValue,
  escrowAmount: NonNegativeMonetaryValue,
  totalAmount: NonNegativeMonetaryValue,

  hasPrincipalAndInterest: z.boolean(),
  hasFees: z.boolean(),
  hasEscrow: z.boolean(),

  paymentDueIds: z.array(PaymentDueId),
  chargeIds: z.array(FeeId),

  paymentConfigId: PaymentConfigId.optional(), // TODO: Deprecate this

  delinquentInterestSettingId: DelinquentInterestSettingId.optional(),
});
export type DefermentWorkoutPlan = z.infer<typeof DefermentWorkoutPlan>;

/**
 * FORBEARANCE WORKOUT PLAN
 * Forbearance is offered when a borrower is experiencing a financial hardship.
 * During this period the borrower’s payments are reduced or suspended, but we'll keep generating payments due and applying as normal.
 * * late fees don't accrue while the plan is active
 * * the loan will still be reportable as delinquent
 *
 * After the forbearance period (successful or failed), the lender must determine whether the borrower is eligible for a payment deferral or flex mod
 */
export const ForbearanceCancellationReason = z.enum([
  'insufficientPayments',
  'noLongerEligible',
  'evaluatingDifferentPlan',
  'loanReinstated',
  'repaymentPlanSelected',
  'defermentSelected',
  'flexModificationSelected',
]);
export type ForbearanceCancellationReason = z.infer<typeof ForbearanceCancellationReason>;

export const ForbearanceBasePlan = WorkoutPlanBase.extend({
  type: z.literal('forbearance'),
  subType: z.enum(['formal', 'disaster', 'special', 'notApplicable']),
  programMoratoriumStartDate: zodJSPlainDate.optional(), // this is for reporting only (ie a specific covid forbearance program started on ## date)
  startDate: zodJSPlainDate,
  originalTermMonths: z.int().positive(), // standard initial terms can go up to 6-months, with option for lender to extend to 12-months (but we're not going to restrict to this standard limit)
  // A plan can be extended multiple times, with the standard maximum being up to 12-months
  // However, GSEs have special programs that can extend for longer periods with explicit approval (ie fannie mae made an exception during covid and allowed 18-month extensions)
  extensions: z.array(
    z.object({
      extensionTermMonths: z.int().positive(), // standard is maximum 12-months, but there are exceptions so leaving this open
      extensionDate: zodJSInstantDate,
    }),
  ),
  // As payments come in, these planPaymentsDue are "fulfilled", but they do not change normal payment application.
  planPaymentsDue: z.array(WorkoutPlanPaymentDue), // will be $0 for suspended forbearance, but this will make tracking associated payments due easier
  workflowInformation: WorkoutPlanWorkflowInformation,
  imminentDefault: z.boolean(), // Indicates the loan has been placed in forbearance because the borrower has been determined to be in imminent default

  terminationDate: zodJSPlainDate.optional(), // early termination scheduled date (plan will be active until the very end of this day)
  cancellationReason: ForbearanceCancellationReason.optional(),
});
const SuspendedForbearancePlan = ForbearanceBasePlan.extend({
  forbearanceType: z.literal('suspended'),
});
export type SuspendedForbearancePlan = z.infer<typeof SuspendedForbearancePlan>;

const ReducedForbearancePlan = ForbearanceBasePlan.extend({
  forbearanceType: z.literal('reduced'),
  partialPaymentAmount: z.number().nonnegative(), // will match planPaymentsDue.total, but is easier to manage displays to store here
});
export type ReducedForbearancePlan = z.infer<typeof ReducedForbearancePlan>;

export const ForbearanceWorkoutPlan = z.discriminatedUnion('forbearanceType', [
  SuspendedForbearancePlan,
  ReducedForbearancePlan,
]);

export type ForbearanceWorkoutPlan = z.infer<typeof ForbearanceWorkoutPlan>;

/**
 * FLEX MODIFICATION WORKOUT PLAN
 */
export const FlexModificationModification = z.object({
  interestRate: NonNegativeFloat,
  loanTerm: NonNegativeInteger,
  unpaidPrincipalBalance: NonNegativeMonetaryValue,
  statementEscrowAmount: NonNegativeMonetaryValue,
  deferredPrincipalAmount: NonNegativeMonetaryValue.optional(), // This is the total capitalized amount. everything (interest, fees, advances, etc) that was rolled into the flex mod is set to deferred principal
});
export type FlexModificationModification = z.infer<typeof FlexModificationModification>;

export const FlexModificationPermanentModification = z.object({
  interestRate: NonNegativeFloat,
  loanTerm: PositiveInteger,
  unpaidPrincipalBalance: PositiveMonetaryValue,
  effectiveDate: zodJSPlainDate,
  deferredPrincipalAmount: NonNegativeMonetaryValue.optional(),
});
export type FlexModificationPermanentModification = z.infer<typeof FlexModificationPermanentModification>;

/**
 * A flex modification is offered to a borrower in order to offer more affordable payment terms.
 * Any outstanding principal, interest, advances and some fees are capitalized into the loan’s unpaid principal
 * balance (UPB) and a new interest rate and/or loan term length is offered to the borrower.
 *
 * The workout plan is used to track the Trial Period Plan in which borrowers must make payments at the modified terms for a specified number of months before the flex modification becomes permanent
 *
 * The loan keeps billing as normal during the trial period. So while we expect the reduced trial payments, the loan is expected to become
 * delinquent during this time (if its not already), and is still reportable as delinquent.
 *
 * Documentation for more details: https://www.figma.com/board/561q5ifrz94gK0THyHr6Du/FNMA-Delinquency-Workflows?node-id=258-69&t=moP6h45puOmHJQ8y-0
 */
export const FlexModificationWorkoutPlan = WorkoutPlanBase.extend({
  type: z.literal('flexModification'),

  reason: z.enum(['naturalDisaster']).optional(),

  // WorkoutPlanBase.startDate = trial start date
  // 0 means there is no trial period (used to model a loan modification): the plan has no trial payments and can
  // be completed immediately to apply the permanent modification.
  trialTermMonths: z.number().int().min(0).max(12), // fannie mae loans are either 3 or 4 months, but possibly other GSE or mortgage providers offer diff terms

  evaluationNoticeSentDate: zodJSPlainDate.optional(), // first payment must be made within 14 days of this date; absent when there is no trial period

  // This is what the loan will be modified to after the trial period. While this workout plan is active, the loan continues to operate with the normal terms, payments due, and delinquency status
  modification: FlexModificationModification,

  // As payments come in, these planPaymentsDue are "fulfilled", but they do not change normal payment application.
  planPaymentsDue: z.array(WorkoutPlanPaymentDue),

  // To start we're going to collect this information up front when creating the workout plan,
  // but, this will be moved to a workflow at some point in the future
  // Figma flow: https://www.figma.com/board/561q5ifrz94gK0THyHr6Du/FNMA-Delinquency-Workflows?node-id=255-564&t=HT8wF2FN9mWfknH1-0
  workflowInformation: WorkoutPlanWorkflowInformation,

  paymentDueIds: z.array(PaymentDueId).optional(), // Make required in the future
  chargeIds: z.array(FeeId).optional(), // Make required in the future
});
export type FlexModificationWorkoutPlan = z.infer<typeof FlexModificationWorkoutPlan>;

export const FlexModificationArrearages = z.object({
  unpaidPrincipal: z.number().positive(),
  unpaidInterest: z.number().nonnegative(),
  unpaidFeesIncludingDeferred: z.number().nonnegative(),
  unpaidAdvancesIncludingDeferred: z.number().nonnegative(),
  currentOutstandingBalance: z.number().positive(),
  capitalizedChargeIds: z.array(FeeId),
  capitalizedPaymentDueIds: z.array(PaymentDueId),
});
export type FlexModificationArrearages = z.infer<typeof FlexModificationArrearages>;

/**
 * REPAYMENT PLAN
 * A repayment plan is offered when a borrower’s temporary financial hardship has ended and they are able to resume making payments.
 * It provides a structured way for the borrower to bring a delinquent loan current over time,
 * rather than paying all past-due amounts at once.
 *
 * Under a repayment plan, past-due payments are spread evenly across a specified number of future billing statements.
 * During the repayment plan period, the loan remains reportable as delinquent until all past-due amounts have been fully repaid.
 */
export const RepaymentPlanCancellationReason = z.enum([
  'insufficientPayments',
  'referringToForeclosure',
  'loanReinstated',
  'evaluatingForFlexMod',
  'evaluatingForDeferment',
  'evaluatingForDisasterFlexMod',
  'evaluatingForDisasterDeferment',
  'noLongerEligible',
  'other',
]);
export type RepaymentPlanCancellationReason = z.infer<typeof RepaymentPlanCancellationReason>;

const RepaymentWorkoutPlanPaymentsDue = WorkoutPlanPaymentDue.extend({
  contractualPaymentAmount: NonNegativeMonetaryValue, // the amount owed on the normal monthly statement, this is just for ease of lookup
  arrearagePaymentAmount: NonNegativeMonetaryValue, // the portion of the plan payment total that is coming from the arrearages
});
export type RepaymentWorkoutPlanPaymentsDue = z.infer<typeof RepaymentWorkoutPlanPaymentsDue>;

const RepaymentWorkoutPlanArrearageBreakdown = z.object({
  total: PositiveMonetaryValue,
  principal: NonNegativeMonetaryValue,
  interest: NonNegativeMonetaryValue,
  escrow: NonNegativeMonetaryValue,
  statementSuspenseCredit: NonNegativeMonetaryValue,
  feesAndAdvances: NonNegativeMonetaryValue,
  monthlyPaymentAmount: PositiveMonetaryValue, // arrearages only, last month may be lower due to rounding
});
export type RepaymentWorkoutPlanArrearageBreakdown = z.infer<typeof RepaymentWorkoutPlanArrearageBreakdown>;

export const RepaymentWorkoutPlan = WorkoutPlanBase.extend({
  type: z.literal('repaymentPlan'),

  startStatementDate: zodJSPlainDate,
  endStatementDate: zodJSPlainDate,

  termMonths: z.number().int().min(1), // we're not enforcing a max
  contractualMonthlyPaymentAmount: PositiveMonetaryValue, // amount owed on the normal monthly statement without the repayment plan
  repaymentMonthlyAmount: PositiveMonetaryValue, // total amount paid per month: contractual + arrearages
  planIncreaseDecimal: z.number(), // increase from contractual payment amount to the new repayment monthly amount
  arrearageBreakdown: RepaymentWorkoutPlanArrearageBreakdown,

  // Advances: always included in the plan
  // Fees: optionally included (but it's an all or nothing option)
  hasFees: z.boolean(), // whether fees are included in the plan
  chargeIds: z.array(FeeId), // charges included in plan creation. if these charges are later deferred/cancelled and/or new charges are added outside of the plan, that does not effect any on the workout plan

  planPaymentsDue: z.array(RepaymentWorkoutPlanPaymentsDue), // length = termMonths
  dAmountPaid: NonNegativeMonetaryValue,

  workflowInformation: WorkoutPlanWorkflowInformation,

  terminationDate: zodJSPlainDate.optional(), // early termination scheduled date (plan will be active until the very end of this day)
  cancellationReason: RepaymentPlanCancellationReason.optional(),
});
export type RepaymentWorkoutPlan = z.infer<typeof RepaymentWorkoutPlan>;

/**
 * CHAPTER 13 BANKRUPTCY WORKOUT PLAN
 * A borrower in Chapter 13 splits their mortgage debt into pre-petition arrears (cured over the
 * plan term, usually by a trustee) and ongoing post-petition installments. This plan is created
 * independently of the Proof of Claim: it computes its own pre/post-petition split on its own
 * `postPetitionSplitEffectiveDate` (the POC, when one exists, is a parallel snapshot — they can,
 * and routinely do, diverge).
 *
 * Deliberately NOT sharing logic with repayment plans beyond WorkoutPlanBase/WorkoutPlanPaymentDue
 * — the two are expected to drift.
 */
export const BankruptcyCh13PaymentConfigChangeReason = z.enum([
  'amountIncrease',
  'amountDecrease',
  'lengthIncrease',
  'lengthDecrease',
]);
export type BankruptcyCh13PaymentConfigChangeReason = z.infer<typeof BankruptcyCh13PaymentConfigChangeReason>;

export const BankruptcyCh13PostPetitionPaymentConfigChangeReason = z.enum([
  'armAdjustment',
  'hoaFeeShortage',
  'insuranceShortage',
  'taxShortage',
  'other',
  'unknown',
]);
export type BankruptcyCh13PostPetitionPaymentConfigChangeReason = z.infer<
  typeof BankruptcyCh13PostPetitionPaymentConfigChangeReason
>;

export const BankruptcyPaymentPayer = z.enum(['borrower', 'trustee']);
export type BankruptcyPaymentPayer = z.infer<typeof BankruptcyPaymentPayer>;

export const BankruptcyCh13PrePetitionPaymentConfig = z.object({
  monthlyPaymentAmount: NonNegativeMonetaryValue,
  payer: BankruptcyPaymentPayer,
  effectiveDate: zodJSPlainDate, // day must match the statement due day
  numberOfMonthsRemaining: NonNegativeInteger,
  createdAt: zodJSInstantDate,
  changeReason: BankruptcyCh13PaymentConfigChangeReason.optional(), // blank on the originating entry
});
export type BankruptcyCh13PrePetitionPaymentConfig = z.infer<typeof BankruptcyCh13PrePetitionPaymentConfig>;

// Deliberately its own type (not shared with the pre-petition config): the two sides' change
// reasons differ and the shapes are expected to drift.
export const BankruptcyCh13PostPetitionPaymentConfig = z.object({
  monthlyPaymentAmount: NonNegativeMonetaryValue,
  payer: BankruptcyPaymentPayer,
  effectiveDate: zodJSPlainDate, // day must match the statement due day
  numberOfMonthsRemaining: NonNegativeInteger,
  createdAt: zodJSInstantDate,
  changeReason: BankruptcyCh13PostPetitionPaymentConfigChangeReason.optional(), // blank on the originating entry and on payer-only changes
});
export type BankruptcyCh13PostPetitionPaymentConfig = z.infer<typeof BankruptcyCh13PostPetitionPaymentConfig>;

export const BankruptcyCh13AgreedOrderSupplement = z.object({
  month: PositiveInteger, // 1-based offset from the agreed order's effectiveDate
  amount: NonNegativeMonetaryValue,
});
export type BankruptcyCh13AgreedOrderSupplement = z.infer<typeof BankruptcyCh13AgreedOrderSupplement>;

/**
 * A court-ordered repayment of missed post-petition installments. The installments it covers
 * (`startDate`..`endDate`) stop being owed, and the money is re-spread as a monthly supplement on
 * top of the ongoing contractual amount from `effectiveDate` forward.
 */
// Deliberately its own enum (not shared with RepaymentPlanCancellationReason).
// 'archived' is shown to the user as "recorded in error" and maps to status 'archived'.
export const BankruptcyCh13PlanCancellationReason = z.enum(['bankruptcyClosed', 'planAdjustment', 'archived', 'other']);
export type BankruptcyCh13PlanCancellationReason = z.infer<typeof BankruptcyCh13PlanCancellationReason>;

export const BankruptcyCh13AgreedOrder = z.object({
  id: BankruptcyAgreedOrderId,

  startDate: zodJSPlainDate, // first statement date covered by the repayment
  endDate: zodJSPlainDate, // last statement date covered by the repayment

  effectiveDate: zodJSPlainDate, // first installment carrying the supplement
  numberOfMonths: PositiveInteger,
  monthlySupplementalPaymentAmount: z.array(BankruptcyCh13AgreedOrderSupplement),

  confirmationDate: zodJSPlainDate,
  cancelledDate: zodJSPlainDate.optional(),
  createdAt: zodJSInstantDate,
});
export type BankruptcyCh13AgreedOrder = z.infer<typeof BankruptcyCh13AgreedOrder>;

export const BankruptcyCh13WorkoutPlan = WorkoutPlanBase.extend({
  type: z.literal('bankruptcyCh13'),
  bankruptcyId: BankruptcyId,

  confirmationDate: zodJSPlainDate, // date the plan was confirmed by the court (user input)
  postPetitionSplitEffectiveDate: zodJSPlainDate, // first post-petition statement date (user input)
  initialContractualLPIDate: zodJSPlainDate, // contractual LPI when the plan was entered (computed)

  prePetition: z.object({
    planPaymentsDue: z.array(WorkoutPlanPaymentDue),
    // The statements/charges marked pre-petition. isPrePetition on each item is derived from
    // membership here (via recalculateDerivedFields), so it clears when the plan ends.
    arrearages: z.object({
      paymentDueIds: z.array(PaymentDueId),
      chargeIds: z.array(FeeId),
    }),
    paymentConfigHistory: z.array(BankruptcyCh13PrePetitionPaymentConfig),
  }),

  // A shadow tracker of the ongoing monthly installments: it never changes how payments apply.
  // paymentConfigHistory is a kept-in-sync copy of the loan's root paymentConfigHistory +
  // escrowPaymentHistory, which the lender may manually override.
  postPetition: z.object({
    // Recalculated from paymentConfigHistory + agreedOrders on every derived-fields pass.
    planPaymentsDue: z.array(WorkoutPlanPaymentDue),
    paymentConfigHistory: z.array(BankruptcyCh13PostPetitionPaymentConfig),
    agreedOrders: z.array(BankruptcyCh13AgreedOrder),
    // Last root contractual components mirrored into paymentConfigHistory (derived — seeded on the
    // first recalc after creation). Sync pushes a new entry only when these move, so a lender's
    // manual override (court-ordered amount that deliberately differs from contractual) survives
    // recalc passes.
    dLastSyncedPrincipalAndInterest: z.number().optional(),
    dLastSyncedMonthlyEscrow: z.number().optional(),
  }),

  terminationDate: zodJSPlainDate.optional(), // lender-selected effective date of the cancellation
  cancellationReason: BankruptcyCh13PlanCancellationReason.optional(),
});
export type BankruptcyCh13WorkoutPlan = z.infer<typeof BankruptcyCh13WorkoutPlan>;

/** ------------------ */
export const WorkoutPlan = z.discriminatedUnion('type', [
  DefermentWorkoutPlan, // Followup delete
  PaymentDeferralWorkoutPlan,
  ForbearanceWorkoutPlan,
  FlexModificationWorkoutPlan,
  RepaymentWorkoutPlan,
  BankruptcyCh13WorkoutPlan,
]);
export type WorkoutPlan = z.infer<typeof WorkoutPlan>;

/** ------------------ */
export const isPaymentDeferralWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is PaymentDeferralWorkoutPlan =>
  workoutPlan.type === 'paymentDeferral';

export const isDefermentWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is DefermentWorkoutPlan =>
  workoutPlan.type === 'paymentDeferment';

export const isForbearanceWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is ForbearanceWorkoutPlan =>
  workoutPlan.type === 'forbearance';

export const isFlexModificationWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is FlexModificationWorkoutPlan =>
  workoutPlan.type === 'flexModification';

// A 0-month flex modification is a direct loan modification with no trial period plan to solicit
export const hasFlexModTrialPeriod = (workoutPlan: Pick<FlexModificationWorkoutPlan, 'trialTermMonths'>): boolean =>
  workoutPlan.trialTermMonths > 0;

export const isRepaymentWorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is RepaymentWorkoutPlan =>
  workoutPlan.type === 'repaymentPlan';

export const isBankruptcyCh13WorkoutPlan = (workoutPlan: WorkoutPlan): workoutPlan is BankruptcyCh13WorkoutPlan =>
  workoutPlan.type === 'bankruptcyCh13';

export const isPaymentPlanWorkoutPlan = (
  workoutPlan: WorkoutPlan,
): workoutPlan is ForbearanceWorkoutPlan | FlexModificationWorkoutPlan | RepaymentWorkoutPlan => {
  switch (workoutPlan.type) {
    case 'paymentDeferment':
    case 'paymentDeferral':
      return false;
    case 'forbearance':
    case 'flexModification':
    case 'repaymentPlan':
      return true;
    // The Chapter 13 plan tracks its dues under prePetition.planPaymentsDue (not a top-level
    // planPaymentsDue) and is driven by its own handlers, so it stays off the shared payment-plan path.
    case 'bankruptcyCh13':
      return false;
    default:
      assertNever(workoutPlan);
  }
};

export const DefermentWorkoutPlanInput = DefermentWorkoutPlan.omit({
  id: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  paymentDueIds: true,
  chargeIds: true,
  principalAmount: true,
  interestAmount: true,
  feeAmount: true,
  escrowAmount: true,
  totalAmount: true,
});
export type DefermentWorkoutPlanInput = z.infer<typeof DefermentWorkoutPlanInput>;

export const WorkoutPlanInput = DefermentWorkoutPlanInput;
export type WorkoutPlanInput = z.infer<typeof WorkoutPlanInput>;
